/**
 * Maps normalized FieldError[] (from extractFieldErrors) to buckets
 * suitable for a form: top-level field errors, nested array triples,
 * and an unmapped fallback list.
 */

import type { FieldError } from './api-errors';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type Triple = { row: number; field: string; msg: string };

/**
 * Sentinel `field` value for a whole-row error with no reliable column.
 * SampleTable checks for this to apply row-level styling + tooltip.
 */
export const ROW_ERROR_FIELD = '__row__';

export type MapFieldErrorsResult = {
  formErrors: Record<string, string>;
  arrayErrors: Record<string, Triple[]>;
  unmapped: string[];
};

export type MapFieldErrorsOptions = {
  /** Names of array fields on the form (e.g. ['samples']). */
  arrayFields: string[];
  /** Which arrayField kind:'row' errors land in; defaults to arrayFields[0]. */
  rowArrayField?: string;
  /** Scan rows for name-substring matches on string-detail loc:['detail'] errors. */
  byNameLookup?: {
    fieldName: string;
    rows: Array<{ name?: string | null }>;
  };
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pushTriple(arrayErrors: Record<string, Triple[]>, arrayField: string, triple: Triple): void {
  if (arrayErrors[arrayField] === undefined) {
    arrayErrors[arrayField] = [];
  }
  // Merge multiple messages for the same row+field rather than appending a duplicate triple:
  // a cell stores a single string (SET_CELL_ERRORS), so a blind append would drop earlier
  // messages when the backend returns several issues for one cell.
  const existing = arrayErrors[arrayField].find(t => t.row === triple.row && t.field === triple.field);
  if (existing) {
    if (!existing.msg.includes(triple.msg)) existing.msg = `${existing.msg}; ${triple.msg}`;
    return;
  }
  arrayErrors[arrayField].push(triple);
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function mapFieldErrorsToForm(errors: FieldError[], options: MapFieldErrorsOptions): MapFieldErrorsResult {
  const result: MapFieldErrorsResult = {
    formErrors: {},
    arrayErrors: {},
    unmapped: [],
  };

  const { arrayFields, rowArrayField, byNameLookup } = options;
  const arrayFieldSet = new Set(arrayFields);
  const defaultArrayField = rowArrayField ?? arrayFields[0];

  for (const error of errors) {
    // -----------------------------------------------------------------------
    // Branch A: kind:'row' — VERIFIED path
    // -----------------------------------------------------------------------
    if (error.kind === 'row') {
      if (typeof defaultArrayField === 'string' && defaultArrayField.length > 0) {
        pushTriple(result.arrayErrors, defaultArrayField, {
          row: error.rowIndex,
          field: ROW_ERROR_FIELD,
          msg: error.msg,
        });
      } else {
        result.unmapped.push(error.msg);
      }
      continue;
    }

    // -----------------------------------------------------------------------
    // Branch B: kind:'loc'
    // -----------------------------------------------------------------------
    const { loc, msg } = error;

    // B1. Array path: ['body', <arrayField>, N, subField] — length 4
    if (
      loc.length === 4 &&
      loc[0] === 'body' &&
      typeof loc[1] === 'string' &&
      arrayFieldSet.has(loc[1]) &&
      typeof loc[2] === 'number' &&
      typeof loc[3] === 'string'
    ) {
      pushTriple(result.arrayErrors, loc[1], {
        row: loc[2],
        field: loc[3],
        msg,
      });
      continue;
    }

    // B2. Top-level field: ['body', field] — length 2, field NOT an arrayField.
    // Accumulate distinct messages for the same field rather than overwriting — FastAPI can
    // emit several validation errors for one field.
    if (loc.length === 2 && loc[0] === 'body' && typeof loc[1] === 'string' && !arrayFieldSet.has(loc[1])) {
      const field = loc[1];
      const existing = result.formErrors[field];
      if (existing === undefined) {
        result.formErrors[field] = msg;
      } else if (!existing.includes(msg)) {
        result.formErrors[field] = `${existing}; ${msg}`;
      }
      continue;
    }

    // B3. String-detail: ['detail'] — length 1, try byNameLookup.
    // Only attempt per-row name matching when the message actually reads like a name
    // conflict. Without this gate a coincidental quoted substring (e.g. a sample literally
    // named 'detail' or a short name appearing in an unrelated server message) would light
    // up the wrong row. Non-conflict string details fall through to `unmapped` (→ toast).
    if (loc.length === 1 && loc[0] === 'detail') {
      const looksLikeNameConflict = /already exist|duplicat|conflict|in use|taken/i.test(msg);
      if (byNameLookup !== undefined && arrayFields.length > 0 && looksLikeNameConflict) {
        const targetField = arrayFields[0];
        let matched = false;

        for (let i = 0; i < byNameLookup.rows.length; i++) {
          const row = byNameLookup.rows[i];
          const name = row.name;
          if (typeof name === 'string' && name.trim().length > 0) {
            // Check for single-quoted substring: '<name>'
            if (msg.includes(`'${name.trim()}'`)) {
              matched = true;
              pushTriple(result.arrayErrors, targetField, {
                row: i,
                field: byNameLookup.fieldName,
                msg,
              });
            }
          }
        }

        if (!matched) {
          result.unmapped.push(msg);
        }
        continue;
      }
    }

    // Fallthrough — unrecognised loc shape
    result.unmapped.push(msg);
  }

  return result;
}
